import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ChakraProvider } from "@chakra-ui/react";
import { Capacitor } from "@capacitor/core";
import { Page } from "../components/Page/Page";
import { system } from "../lib/theme/System";
import {
  Toaster,
  ToastRoot,
  ToastTitle,
  ToastDescription,
  ToastCloseTrigger,
  ToastIndicator,
} from "@chakra-ui/react";
import { toaster } from "../lib/toaster";
import { Contact } from "../pages/marketing/Contact/Contact";
import { SignIn } from "../pages/marketing/SignIn/SignIn";
import { Policy } from "../pages/marketing/Policy/Policy";
import { Legal } from "../pages/marketing/Legal/Legal";
import { EventV2 } from "../pages/marketing/EventV2";
import { UserProfile } from "../pages/marketing/UserProfile/UserProfile";
import { KeepAliveTabs } from "../components/KeepAliveTabs/KeepAliveTabs";
import { Profile } from "../pages/marketing/Profile/Profile";
import { Home } from "../pages/marketing/Home/Home";
import { FAQ } from "../pages/marketing/FAQ/FAQ";
import { ClaimRegistration } from "../pages/marketing/ClaimRegistration/ClaimRegistration";
import { AttendanceConfirmation } from "../pages/marketing/AttendanceConfirmation/AttendanceConfirmation";
import { SparkInvite } from "../pages/marketing/SparkInvite/SparkInvite";
import { ClaimRegistrationComplete } from "../pages/marketing/ClaimRegistration/ClaimRegistrationComplete";
import { AppLoader } from "../pages/app/AppLoader/AppLoader";
import { LoggedOut } from "../pages/app/LoggedOut/LoggedOut";
import { activeClientAdapter } from "../lib/payments";

const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: "https://us.i.posthog.com",
    // Page.tsx captures $pageview on every route change (SPA-aware). Disable
    // the SDK's automatic pageview so a hard load isn't double-counted.
    capture_pageview: false,
    loaded: (posthog) => {
      // @ts-expect-error posthog not defined on window, but used via posthog toolbar
      window.posthog = posthog;
    },
  });
}

// Unregister any old service workers left over from vite-plugin-pwa
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

// Warm up the active processor's SDK after the initial page settles.
// PaymentForm is deep in the registration flow, so by the time a user reaches
// it the script is already loaded — avoiding a user-visible wait on submit.
// Fire-and-forget: failures here are retried when PaymentForm mounts.
const warmPaymentSdk = () => {
  activeClientAdapter()
    .preload()
    .catch(() => {
      /* noop — retried on PaymentForm mount */
    });
};
if (document.readyState === "complete") {
  warmPaymentSdk();
} else {
  window.addEventListener("load", warmPaymentSdk, { once: true });
}

let routes = [];
if (Capacitor.isNativePlatform()) {
  routes = [
    {
      path: "",
      element: <AppLoader />,
    },
    {
      path: "app/logged-out",
      element: <LoggedOut />,
    },
    {
      path: "/",
      element: <Page />,
      children: [
        {
          path: "sign-in",
          element: <SignIn />,
        },
        {
          path: "events",
          element: <KeepAliveTabs />,
        },
        {
          path: "spark",
          element: <KeepAliveTabs />,
        },
        {
          path: "matches",
          element: <KeepAliveTabs />,
        },
        {
          // `/matches/:userId` is rendered as an overlay inside Page.tsx so
          // the Matches list stays mounted behind it. KeepAliveTabs routes
          // the underlying tab based on the path.
          path: "matches/:userId",
          element: <KeepAliveTabs />,
        },
        {
          // `/event/:sparkId/match` is rendered as an overlay inside Page.tsx so
          // the originating Events page (and the MatchFillWidget's cards)
          // stay mounted, keeping framer-motion's `layoutId` pair intact for
          // a shared-element morph. KeepAliveTabs keeps Events visible here.
          path: "event/:sparkId/match",
          element: <KeepAliveTabs />,
        },
        {
          // An event's "Spark before you go" deck, rendered as an overlay in
          // Page.tsx over the Events tab, like /event/:sparkId/match.
          path: "event/:sparkId/spark",
          element: <KeepAliveTabs />,
        },
        {
          path: "profile/:userId",
          element: <UserProfile />,
        },
        {
          path: "profile",
          element: <Profile />,
        },
        {
          path: "settings/*",
          element: <KeepAliveTabs />,
        },
        {
          path: "legal",
          element: <Legal />,
        },
        {
          path: "policy/:slug",
          element: <Policy />,
        },
        {
          path: "invite/:invitationId",
          element: <SparkInvite />,
        },
      ],
    },
    {},
  ];
} else {
  routes = [
    {
      path: "/",
      element: <Page />,
      children: [
        {
          path: "",
          element: <Home />,
        },
        {
          path: "app/logged-out",
          element: <LoggedOut />,
        },
        {
          path: "admin/*",
          async lazy() {
            const { Admin } = await import("../pages/admin/Admin/Admin");
            return { Component: Admin };
          },
        },
        {
          path: "host/*",
          async lazy() {
            const { Host } = await import("../pages/host/Host/Host");
            return { Component: Host };
          },
        },
        {
          path: "team",
          async lazy() {
            const { Team } = await import("../pages/marketing/Team/Team");
            return { Component: Team };
          },
          children: [],
        },
        {
          path: "partner/*",
          async lazy() {
            const { Partner } = await import(
              "../pages/partner/Partner/Partner"
            );
            return { Component: Partner };
          },
        },
        {
          path: "faq",
          element: <FAQ />,
        },
        {
          path: "contact",
          element: <Contact />,
        },
        {
          path: "spark/atlanta/:sparkId",
          element: <EventV2 />,
        },
        {
          // `/event/atlanta/:sparkId` is rendered as an overlay inside
          // Page.tsx so the Events list stays mounted behind it. KeepAliveTabs
          // keeps Events visible here — same pattern as /event/:sparkId/match
          // and /matches/:userId.
          path: "event/atlanta/:sparkId",
          element: <KeepAliveTabs />,
        },
        {
          path: "sign-in",
          element: <SignIn />,
        },
        {
          path: "events",
          element: <KeepAliveTabs />,
        },
        {
          path: "spark",
          element: <KeepAliveTabs />,
        },
        {
          path: "matches",
          element: <KeepAliveTabs />,
        },
        {
          // `/matches/:userId` is rendered as an overlay inside Page.tsx so
          // the Matches list stays mounted behind it. KeepAliveTabs routes
          // the underlying tab based on the path.
          path: "matches/:userId",
          element: <KeepAliveTabs />,
        },
        {
          // `/event/:sparkId/match` is rendered as an overlay inside Page.tsx so
          // the originating Events page (and the MatchFillWidget's cards)
          // stay mounted, keeping framer-motion's `layoutId` pair intact for
          // a shared-element morph. KeepAliveTabs keeps Events visible here.
          path: "event/:sparkId/match",
          element: <KeepAliveTabs />,
        },
        {
          // An event's "Spark before you go" deck, rendered as an overlay in
          // Page.tsx over the Events tab, like /event/:sparkId/match.
          path: "event/:sparkId/spark",
          element: <KeepAliveTabs />,
        },
        {
          path: "profile/:userId",
          element: <UserProfile />,
        },
        {
          path: "profile",
          element: <Profile />,
        },
        {
          path: "settings/*",
          element: <KeepAliveTabs />,
        },
        {
          path: "legal",
          element: <Legal />,
        },
        {
          path: "policy/:slug",
          element: <Policy />,
        },
        {
          path: "register/complete",
          element: <ClaimRegistrationComplete />,
        },
        {
          path: "register/:token",
          element: <ClaimRegistration />,
        },
        {
          path: "attending/:token",
          element: <AttendanceConfirmation />,
        },
        {
          path: "invite/:invitationId",
          element: <SparkInvite />,
        },
      ],
    },
    {},
  ];
}

const router = createBrowserRouter(routes);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <ChakraProvider value={system}>
        <RouterProvider router={router} />
        <Toaster toaster={toaster}>
          {(toast) => (
            <ToastRoot>
              <ToastIndicator />
              <ToastTitle>{toast.title}</ToastTitle>
              <ToastDescription>{toast.description}</ToastDescription>
              <ToastCloseTrigger />
            </ToastRoot>
          )}
        </Toaster>
      </ChakraProvider>
    </PostHogProvider>
  </React.StrictMode>,
);
